import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pulse, ArrowClockwise, MagnifyingGlass, DownloadSimple, User, Lightning } from "@phosphor-icons/react";
import { withDatePrefix } from "@/lib/downloadFilename";
import { dmY } from "@/lib/dateFmt";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";

const fmt = (n) => n == null || n === "" ? "" : new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n));

const ACTION_LABELS = {
    "upload.create": "Slip Uploaded",
    "upload.verify": "Slip Verified",
    "upload.reject": "Slip Rejected",
    "upload.delete": "Slip Deleted",
    "cash.receipt": "Payment Received",
    "payout.create": "Payment Made",
    "payout.bulk": "Bulk Payments Made",
    "transaction.update": "Transaction Edited",
    "transaction.rectify": "Payment Rectified",
    "transaction.delete": "Transaction Deleted",
    "user.create": "User Created",
    "user.delete": "User Deleted",
    "staff.create": "Staff User Created",
    "staff.update": "Staff User Updated",
    "staff.deactivate": "Staff Deactivated",
    "staff.activate": "Staff Activated",
    "party.create": "Party Created",
    "party.update": "Party Updated",
    "party.delete": "Party Deleted",
    "admin.create": "Admin Created",
    "admin.update": "Admin Updated",
    "admin.delete": "Admin Deleted",
};

const fmtWhen = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return new Intl.DateTimeFormat("en-GB", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit", hour12: false,
            timeZone: "Asia/Kolkata",
        }).format(d).replace(",", "");
    } catch { return iso; }
};

const istToday = () => {
    const now = new Date();
    const utcMs = now.getTime() + now.getTimezoneOffset() * 60_000;
    const ist = new Date(utcMs + 5.5 * 60 * 60_000);
    const p = (n) => String(n).padStart(2, "0");
    return `${ist.getFullYear()}-${p(ist.getMonth() + 1)}-${p(ist.getDate())}`;
};
const istDaysAgo = (n) => {
    const now = new Date();
    const utcMs = now.getTime() + now.getTimezoneOffset() * 60_000;
    const ist = new Date(utcMs + 5.5 * 60 * 60_000 - n * 24 * 60 * 60_000);
    const p = (x) => String(x).padStart(2, "0");
    return `${ist.getFullYear()}-${p(ist.getMonth() + 1)}-${p(ist.getDate())}`;
};

/**
 * v1.27.75 — User Activity Log report. Shows every audited action by
 * every user in the current company (Super User sees across companies).
 * Backed by GET /api/activity-log?date_from=&date_to= — already
 * scoped by ScopedDB on the backend.
 */
export default function UserActivityLog() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateFrom, setDateFrom] = useState(istDaysAgo(7));
    const [dateTo, setDateTo] = useState(istToday());
    const [query, setQuery] = useState("");
    // v1.27.76 — Per-user and per-action drill-down filters.
    const [userFilter, setUserFilter] = useState("__all__");
    const [actionFilter, setActionFilter] = useState("__all__");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            const r = await api.get("/activity-log", { params });
            setRows(r.data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load activity");
        } finally { setLoading(false); }
    }, [dateFrom, dateTo]);
    useEffect(() => { load(); }, [load]);

    // v1.27.76 — Build the two dropdown option lists from whatever the
    // current date range returned. Ordering: alphabetical for users;
    // most-active-first for actions so the busy ones surface at the top.
    const userOptions = useMemo(() => {
        const map = new Map();
        for (const r of rows) {
            const key = r.user_email_current || r.user_email || r.user_id;
            if (!key) continue;
            if (!map.has(key)) {
                map.set(key, {
                    key,
                    label: r.user_name ? `${r.user_name} · ${key}` : key,
                });
            }
        }
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [rows]);
    const actionOptions = useMemo(() => {
        const counts = new Map();
        for (const r of rows) {
            const key = r.action || "unknown";
            counts.set(key, (counts.get(key) || 0) + 1);
        }
        return Array.from(counts.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([key, count]) => ({ key, count, label: ACTION_LABELS[key] || key }));
    }, [rows]);

    const filtered = rows.filter((r) => {
        if (userFilter !== "__all__") {
            const key = r.user_email_current || r.user_email || r.user_id;
            if (key !== userFilter) return false;
        }
        if (actionFilter !== "__all__" && r.action !== actionFilter) return false;
        if (!query) return true;
        const q = query.toLowerCase();
        return [r.user_name, r.user_email_current, r.user_email, r.party_name, r.action, r.details]
            .some((v) => (v || "").toLowerCase().includes(q));
    });

    const exportCsv = () => {
        const header = ["When", "User", "Email", "Action", "Party", "Amount", "Details"].join(",");
        const csv = [header, ...filtered.map((r) => [
            fmtWhen(r.timestamp),
            (r.user_name || "").replace(/,/g, " "),
            (r.user_email_current || r.user_email || ""),
            (ACTION_LABELS[r.action] || r.action).replace(/,/g, " "),
            (r.party_name || "").replace(/,/g, " "),
            r.amount != null ? r.amount : "",
            `"${(r.details || "").replace(/"/g, "'")}"`,
        ].join(","))].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = withDatePrefix(`user-activity-${dateFrom}-to-${dateTo}.csv`); a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-4" data-testid="user-activity-log-page">
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700">
                    <Pulse size={22} weight="bold" />
                </div>
                <div className="flex-1">
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Reports</div>
                    <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">User Activity Log</h1>
                    <p className="mt-0.5 text-sm text-slate-500">Audit trail of every write action performed in your company by any user.</p>
                </div>
                <Button variant="outline" onClick={exportCsv} data-testid="user-activity-export">
                    <DownloadSimple size={14} className="mr-2" /> Export CSV
                </Button>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-slate-500">From</Label>
                        <Input type="date" data-testid="ua-date-from" value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-slate-500">To</Label>
                        <Input type="date" data-testid="ua-date-to" value={dateTo} min={dateFrom} max={istToday()} onChange={(e) => setDateTo(e.target.value)} className="mt-1" />
                    </div>
                    <div className="sm:col-span-2">
                        <Label className="text-[10px] uppercase tracking-widest text-slate-500">Search user, party or action</Label>
                        <div className="relative mt-1">
                            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input data-testid="ua-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g. Sharma Traders, upload, admin" className="pl-8" />
                        </div>
                    </div>
                    {/* v1.27.76 — Drill-down dropdowns */}
                    <div className="sm:col-span-2">
                        <Label className="text-[10px] uppercase tracking-widest text-slate-500">
                            <User size={11} weight="fill" className="mr-1 inline align-middle" />
                            User
                        </Label>
                        <Select value={userFilter} onValueChange={setUserFilter}>
                            <SelectTrigger className="mt-1" data-testid="ua-user-filter">
                                <SelectValue placeholder="All users" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__" data-testid="ua-user-filter-all">All users ({rows.length})</SelectItem>
                                {userOptions.map((u) => (
                                    <SelectItem key={u.key} value={u.key} data-testid={`ua-user-filter-${u.key}`}>
                                        {u.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="sm:col-span-2">
                        <Label className="text-[10px] uppercase tracking-widest text-slate-500">
                            <Lightning size={11} weight="fill" className="mr-1 inline align-middle" />
                            Action
                        </Label>
                        <Select value={actionFilter} onValueChange={setActionFilter}>
                            <SelectTrigger className="mt-1" data-testid="ua-action-filter">
                                <SelectValue placeholder="All actions" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__" data-testid="ua-action-filter-all">All actions</SelectItem>
                                {actionOptions.map((a) => (
                                    <SelectItem key={a.key} value={a.key} data-testid={`ua-action-filter-${a.key}`}>
                                        {a.label} <span className="ml-1 text-[10px] text-slate-500">· {a.count}</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="text-[11px] text-slate-500" data-testid="ua-result-count">
                        Showing <b className="text-slate-800">{filtered.length}</b> of {rows.length} events
                        {(userFilter !== "__all__" || actionFilter !== "__all__" || query) && (
                            <button
                                type="button"
                                onClick={() => { setUserFilter("__all__"); setActionFilter("__all__"); setQuery(""); }}
                                className="ml-2 text-blue-600 hover:underline"
                                data-testid="ua-clear-filters"
                            >
                                Clear filters
                            </button>
                        )}
                    </div>
                    <Button variant="outline" onClick={load} size="sm" data-testid="ua-refresh"><ArrowClockwise size={14} className="mr-2" /> {loading ? "Loading…" : "Refresh"}</Button>
                </div>
            </div>

            {loading ? (
                <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">Loading…</div>
            ) : filtered.length === 0 ? (
                <EmptyState icon={Pulse} title="No activity in range" description="No user actions were recorded in this range. Try widening the dates." />
            ) : (
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="px-3 py-2 font-medium">When</th>
                                    <th className="px-3 py-2 font-medium">User</th>
                                    <th className="px-3 py-2 font-medium">Action</th>
                                    <th className="px-3 py-2 font-medium">Party</th>
                                    <th className="px-3 py-2 text-right font-medium">Amount</th>
                                    <th className="px-3 py-2 font-medium">Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r, i) => (
                                    <tr key={r.id || i} className="border-t border-slate-100" data-testid={`ua-row-${i}`}>
                                        <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtWhen(r.timestamp)}</td>
                                        <td className="px-3 py-2">
                                            <div className="font-medium text-slate-800">{r.user_name || "—"}</div>
                                            <div className="font-mono text-[10px] text-slate-500">{r.user_email_current || r.user_email}</div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="inline-flex rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-800">
                                                {ACTION_LABELS[r.action] || r.action}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-slate-700">{r.party_name || "—"}</td>
                                        <td className="px-3 py-2 text-right tabular-nums text-slate-800">{fmt(r.amount)}</td>
                                        <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{r.details || ""}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
